// ChatCard — interactive cards rendered INSIDE an assistant message.
//
// The simone assistant is a dynamic surface, not a text box: it can attach a
// `cards` array to its message (via the render_cards / ask_user client tools)
// and ChatMessage renders each card through this dispatcher. We compose platform
// PRIMITIVES (CommonCard, Avatar, AgentAvatar, Img) into chat-native cards with
// their own onClick — the heavy page cards (TicketsCard, PersonCard) read
// page-root state the chat root doesn't have, so we don't mount them directly.
//
// Each card object becomes the dispatcher's `s` (childrenAs:'state' from
// ChatMessage.Cards). Card shapes:
//   { type:'tickets',    items:[{ id,title,state,priority,number,assignee }] }
//   { type:'people',     items:[{ name,email,kind:'agent'|'human',role }] }
//   { type:'thumbnails', items:[{ url,caption,href }] }
//   { type:'choice',     prompt, options:[{label,value,primary}], _cardId, _callId,
//                        _resolved, _chosen, _chosenLabel, _superseded, _closedBy,
//                        _written }
//   { type:'confirm',    prompt, confirmLabel, cancelLabel, _cardId, _callId,
//                        _resolved, _chosen, _chosenLabel, _superseded, _closedBy,
//                        _edit }
// A question card (choice / confirm) carries the call id of the step that
// asked (`_callId`): ChatMessage draws it under that step (ChatToolStep).

// ── One ticket → a clickable compact card; opens the ticket on /tickets. ──
const TICKET_CARD = {
  tag: 'button',
  type: 'button',
  extends: 'CommonCard',
  theme: (el, s) => el.scope.surface(s),
  width: '100%',
  textAlign: 'left',
  border: '0',
  cursor: 'pointer',
  flow: 'x',
  gap: 'Z',
  align: 'center left',
  // A CLICKABLE CARD lifts on the `float` shadow under the pointer — the
  // house interactive-card cue (ItemPreview, IntegrationCategoryCard). The
  // card is the brand `card` surface now (CommonCard), and the `hover`
  // wash it used REPLACED that fill: in light it stepped 0.008 luminance
  // off the card at rest, no visible change.
  transition: 'Z defaultBezier box-shadow',
  ':hover': { shadow: 'float' },
  onClick: (e, el, s) => {
    // Open the detail modal ONLY with a full ticket resolved from the live list
    // (we're on /tickets) — the same safe path the dashboard task widgets use.
    // The heavy TicketsDetailModal HANGS the renderer when seeded with only the
    // card's partial data (it reads fields/relations the card doesn't carry), so
    // when the ticket isn't loaded on this page navigate to the board instead —
    // a normal page load, not a synchronous rebuild — rather than opening the
    // modal with a stub.
    try {
      const root = el.scope.rootOf(el)
      const full =
        root && Array.isArray(root.tickets)
          ? root.tickets.find((t) => String(t.id) === String(s.id))
          : null
      if (full) el.call('openTicketDetailModal', full)
      else el.call('openAppPage', '/tasks')
    } catch (_) {}
  },
  PvDot: {
    boxSize: 'Y',
    round: '50%',
    flexShrink: '0',
    background: (el, s) => el.scope.stateColor(s.state)
  },
  PvBody: {
    flow: 'y',
    gap: 'V',
    flex: '1 1 0',
    minWidth: '0',
    // The ticket's TITLE — primary text, never cut (the overflow law,
    // HOME_AMBIENT_SKIN_SPEC §3b rule 8): it wraps inside the card.
    PvTitle: {
      text: (el, s) => s.title || el.call('polyglot', 'ai.chatCard.ticketFallback'),
      fontSize: 'Z1',
      fontWeight: '700',
      color: 'title',
      overflowWrap: 'anywhere'
    },
    PvMeta: {
      flow: 'x',
      gap: 'Y',
      align: 'center left',
      PvNum: {
        text: (el, s) => (s.number != null && s.number !== '' ? '#' + s.number : ''),
        fontSize: 'Z',
        color: 'caption'
      },
      PvState: {
        text: (el, s) => String(s.state || '').replace(/_/g, ' '),
        fontSize: 'Z',
        color: 'caption'
      },
      PvPriority: {
        if: (el, s) => !!s.priority,
        text: (el, s) => '· ' + s.priority,
        fontSize: 'Z',
        color: 'caption'
      }
    }
  },
  Avatar: {
    if: (el, s) => !!s.assignee,
    avatarType: 'initials',
    boxSize: 'C C',
    flexShrink: '0',
    src: (el, s) => el.call('avatarUrl', s.assignee)
  }
}

// ── One person / agent → an avatar chip; agents get the robot AgentAvatar.
// Clicking an AGENT chip directs the user's next message at that agent (sets
// _targetAgentId, which the transport forwards so the server resolves that
// agent's persona). Pass the agent's id on the card item to enable this. ──
const PERSON_CHIP = {
  extends: 'CommonCard',
  theme: (el, s) => el.scope.surface(s),
  // A div (CommonCard's default tag). `tag:` is fixed at element creation — a
  // function value doesn't apply and left the chip unrendered. onClick works on a
  // div; agent chips target the agent, human chips are inert.
  cursor: (el, s) => (s.kind === 'agent' ? 'pointer' : 'default'),
  // Only an AGENT chip is clickable, so only it lifts (the `float` cue of a
  // clickable card); a human chip stays still. The `overlay` wash it used
  // replaced the card fill (0.011 luminance darker than the card in dark).
  transition: 'Z defaultBezier box-shadow',
  ':hover': { shadow: (el, s) => (s.kind === 'agent' ? 'float' : undefined) },
  attr: {
    role: (el, s) => (s.kind === 'agent' ? 'button' : null),
    tabindex: (el, s) => (s.kind === 'agent' ? '0' : null),
    'aria-label': (el, s) =>
      s.kind === 'agent' ? 'Message ' + (s.name || s.title || 'agent') : null
  },
  onClick: (e, el, s) => {
    if (s.kind !== 'agent') return
    try {
      const root = el.scope.rootOf(el)
      if (root && root.update)
        root.update({ _targetAgentId: s.id || null, _targetAgentName: s.name || null })
    } catch (_) {}
  },
  onKeydown: (e, el, s) => {
    if (s.kind !== 'agent') return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      try {
        const root = el.scope.rootOf(el)
        if (root && root.update)
          root.update({ _targetAgentId: s.id || null, _targetAgentName: s.name || null })
      } catch (_) {}
    }
  },
  flow: 'x',
  gap: 'Y',
  align: 'center left',
  padding: 'V Z',
  PersonAvatar: {
    extends: 'Avatar',
    if: (el, s) => s.kind !== 'agent',
    avatarType: 'initials',
    boxSize: 'C C',
    flexShrink: '0',
    src: (el, s) => el.call('avatarUrl', s.name || s.email || '?')
  },
  BotAvatar: {
    extends: 'AgentAvatar',
    if: (el, s) => s.kind === 'agent',
    boxSize: 'C C',
    flexShrink: '0'
  },
  PvWho: {
    flow: 'y',
    gap: 'V',
    minWidth: '0',
    // A person's NAME — primary text: it wraps (it was one unclipped line
    // that ran out of the card on a long name or an email).
    PvName: {
      text: (el, s) => s.name || s.email || el.call('polyglot', 'ai.chatCard.memberFallback'),
      fontSize: 'Z1',
      fontWeight: '700',
      color: 'title',
      overflowWrap: 'anywhere'
    },
    PvRole: {
      if: (el, s) => !!(s.role || s.kind === 'agent'),
      text: (el, s) =>
        s.role || (s.kind === 'agent' ? el.call('polyglot', 'ai.chatCard.agentRole') : ''),
      fontSize: 'Z',
      color: 'caption'
    }
  }
}

// ── One thumbnail (optionally a link). Static `tag: 'a'` (an anchor with no
// href is an inert inline element, which is fine) — a function `tag:` doesn't
// apply at creation. ──
const THUMB = {
  tag: 'a',
  href: (el, s) => s.href || null,
  // Static — `target` on an href-less anchor is inert (same reasoning as `tag`).
  target: '_blank',
  flow: 'y',
  gap: 'V',
  width: 'F',
  textDecoration: 'none',
  PvImgWrap: {
    width: '100%',
    height: 'E',
    round: 'Z',
    overflow: 'hidden',
    background: 'overlay',
    Img: {
      extends: 'Img',
      src: (el, s) => s.url,
      objectFit: 'cover',
      width: '100%',
      height: '100%'
    }
  },
  // Secondary meta: one line, the whole caption on hover (rule 8).
  PvCap: {
    if: (el, s) => !!s.caption,
    text: (el, s) => s.caption,
    title: (el, s) => s.caption,
    fontSize: 'Z',
    color: 'caption',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
}

// ── One insurance policy → a compact clickable card; opens /policies. ──
// Visual language mirrors TICKET_CARD: CommonCard shell, flow x, same gap/
// align/hover token. Initials badge via el.call('policyInitials'), premium
// via el.call('formatPolicyMoney'), status pill themed via el.call('policyStatus').
const POLICY_CARD = {
  tag: 'button',
  type: 'button',
  extends: 'CommonCard',
  theme: (el, s) => el.scope.surface(s),
  width: '100%',
  textAlign: 'left',
  border: '0',
  cursor: 'pointer',
  flow: 'x',
  gap: 'Z',
  align: 'center left',
  // A CLICKABLE CARD lifts on the `float` shadow under the pointer — the
  // house interactive-card cue (ItemPreview, IntegrationCategoryCard). The
  // card is the brand `card` surface now (CommonCard), and the `hover`
  // wash it used REPLACED that fill: in light it stepped 0.008 luminance
  // off the card at rest, no visible change.
  transition: 'Z defaultBezier box-shadow',
  ':hover': { shadow: 'float' },
  onClick: (e, el) => {
    try {
      el.call('openAppPage', '/policies')
    } catch (_) {}
  },
  // Initials disc — NOT keyed `Avatar`: that auto-extends the Avatar built-in,
  // whose `src` expects an image URL (initials text just renders the "Avatar"
  // placeholder). Plain themed disc, matching the /policies table row.
  Initials: {
    flow: 'y',
    align: 'center center',
    width: 'C',
    height: 'C',
    flexShrink: '0',
    round: '50%',
    theme: 'tertiary',
    fontSize: 'Z',
    fontWeight: '700',
    color: 'title',
    text: (el, s) => el.call('policyInitials', s.insured)
  },
  PvBody: {
    flow: 'y',
    gap: 'V',
    flex: '1 1 0',
    minWidth: '0',
    // The policy's identifier and product are one-line meta with their
    // whole text on hover; the insured person's NAME is primary and wraps
    // (the overflow law, §3b rule 8).
    PvNumber: {
      text: (el, s) => '#' + s.number,
      title: (el, s) => '#' + s.number,
      fontSize: 'Z1',
      fontWeight: '700',
      fontFamily: 'monospace',
      color: 'title',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    PvInsured: {
      text: (el, s) => s.insured || '',
      fontSize: 'Z1',
      fontWeight: '700',
      color: 'title',
      overflowWrap: 'anywhere'
    },
    PvProduct: {
      text: (el, s) => s.product || '',
      title: (el, s) => s.product || null,
      fontSize: 'Z',
      color: 'caption',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  },
  PvRight: {
    flow: 'y',
    gap: 'V',
    align: 'flex-end',
    flexShrink: '0',
    PvPremium: {
      text: (el, s) => el.call('formatPolicyMoney', s.premium, s.currency),
      fontSize: 'Z1',
      fontWeight: '700',
      color: 'title'
    },
    PvStatus: {
      padding: 'V Z',
      round: 'Y',
      fontSize: 'Y2',
      fontWeight: '700',
      // Theme drives the pill colour (success=green, warning=orange, alert=red).
      theme: (el, s) => el.call('policyStatus', s).theme,
      text: (el, s) => el.call('polyglot', el.call('policyStatus', s).label)
    }
  }
}

// ── One answer of a question card (choice / confirm). A press answers: on a
// live card resolveChatCard resumes the waiting tool; on an answered card
// reopened by Change, changeChatCardAnswer sends the new answer as the
// person's next message (scope.askPick). The card shows these only while it
// is open, so an answer is never inert or dimmed.
//
// THE HOUSE CAPSULE at the compact size (Nika 2026-10-28: "improve design, ui
// and UX of confirmation card"): AppCapsuleBtn owns the geometry — 36px
// (`controlHCompact`), the 44pt floor on a coarse pointer, the press, the
// focus ring, a label that wraps. It was a hand-rolled button: a `blue` fill
// for the primary answer (hue never fills a chrome action), the `Z` letter
// radius, weight 700, an opacity hover and no touch floor. The primary answer
// is the ink capsule (`theme: 'primary'`, its own hover and press steps);
// the others are the tonal `layer` capsule with the `selected` step — the
// AppCapsuleBtnPrimary / AppCapsuleBtnToolbar roles, as exclusive classes: a
// childrenAs:'state' list takes ONE `childExtends`, and two roles must never
// meet on one element (the tonal hover would paint over the ink capsule's
// own). While Change has an answered card open, its CURRENT answer is the
// chosen segment of a choice control — the brand inversion (`highlight` fill,
// `highlight-reversed` ink), `aria-pressed` — so the person sees what they
// are changing.
const CHOICE_BTN = {
  extends: 'AppCapsuleBtn',
  compact: true,
  border: 'none',
  isPrimary: (el, s) => !!s.primary && !s.chosen,
  isTonal: (el, s) => !s.primary && !s.chosen,
  isChosen: (el, s) => !!s.chosen,
  '.isPrimary': { theme: 'primary' },
  '.isTonal': {
    theme: 'layer',
    color: 'title',
    ':hover': { background: 'selected' },
    ':active': { background: 'selected' }
  },
  '.isChosen': { background: 'highlight', color: 'highlight-reversed' },
  aria: { pressed: (el, s) => (s.chosen ? 'true' : null) },
  text: (el, s) => s.label,
  onClick: (e, el, s) => {
    e.preventDefault()
    el.scope.askPick(el, s.value, s.label)
  }
}

// ── Dispatcher: render one card by its `type`. Referenced as `ChatCard` from
// ChatMessage.Cards (childExtends). ──
export const ChatCard = {
  // FA204 / frank trap — STATE_COLOR/stateColor/rootOf/choiceOptions used to
  // be bare module-scope const/function read from inside TICKET_CARD.onClick,
  // TICKET_CARD.PvDot.background, PERSON_CHIP.onClick/onKeydown, and
  // PvCardChoice.PvOptions.children (all serialized factories nested under
  // this file's single top-level export); they read as undefined once this
  // component is frank.toJSON'd for platform delivery. Moved into `scope`,
  // called via `el.scope.<name>`. TICKET_CARD/PERSON_CHIP/THUMB/POLICY_CARD/
  // CHOICE_BTN themselves stay module-scope plain object literals — they're
  // referenced directly as `childExtends: X` values (not read from inside a
  // nested function), so frank inlines their structure at build time; only
  // the FUNCTIONS nested inside them get toString'd, which is why those
  // functions (not the containing objects) needed the el.scope rewrite.
  scope: {
    // The surface of a chat card follows the chat's posture (Chat.js
    // `theme`). In the workspace drawer the chat is a `common-box` SHEET, so
    // a card on it is the brand `card` (CommonCard): 1.19:1 dark / 1.13:1
    // light. Standalone (the extension panels) the chat IS the page, the
    // `document` ground — gray15 in light, the `card` fill itself (1.00:1)
    // — so a card there is the raised surface of a ground, `common-box`
    // (brand law: a card sits inside a sheet; on the stage the raised
    // surface is common-box): 1.13:1 light.
    surface(s) {
      return s.root && s.root.workspaceEmbed ? 'card' : 'common-box'
    },

    // Ticket state → a design-system colour for the status dot.
    STATE_COLOR: {
      not_started: 'gray',
      backlog: 'gray',
      todo: 'gray',
      unsynced: 'gray',
      started: 'blue',
      in_progress: 'blue',
      'in-progress': 'blue',
      in_review: 'orange',
      review: 'orange',
      ready_to_test: 'orange',
      ready_for_staging: 'orange',
      blocked: 'red',
      done: 'green',
      completed: 'green',
      closed: 'green',
      merged: 'green'
    },
    stateColor(st) {
      return (
        this.STATE_COLOR[
          String(st || '')
            .toLowerCase()
            .replace(/[\s-]+/g, '_')
        ] || 'gray'
      )
    },

    // Resolve the chat root's state.root from any card element. Cards render
    // under ChatMessage's childrenAs:'state' subtree; el.state.root is the
    // fast path, el.getRoot() the fallback.
    rootOf(el) {
      return (
        (el && el.state && el.state.root) ||
        (el && el.getRoot && el.getRoot() && el.getRoot().state && el.getRoot().state.root) ||
        null
      )
    },

    // THE QUESTION TAKES THE KEYBOARD (round 1b, ledger BLD-18). Has THIS
    // question (a choice / confirm card, by its id) taken focus before? The
    // marker lives on the chat's root state, so a re-render that mounts the
    // same card again never moves focus a second time.
    askFocused(s) {
      const root = s && s.root
      return !!(root && s._cardId && root.__chatAskFocused === s._cardId)
    },
    // May a live question take focus now? Only an unanswered card that has not
    // had it yet, and never from a person who is typing (a field that holds
    // text keeps the keyboard) or from another open layer (a dialog, a modal
    // sheet). Read when the card is created, before its node exists.
    askTakesFocus(el, s) {
      if (!s || s._resolved || !s._cardId || this.askFocused(s)) return false
      const doc = typeof document === 'undefined' ? null : document
      if (!doc) return false
      const active = doc.activeElement
      if (!active || active === doc.body || active === doc.documentElement) return true
      const tag = String(active.tagName || '').toUpperCase()
      const field = tag === 'TEXTAREA' || tag === 'INPUT' || active.isContentEditable === true
      const held = field ? (active.value != null ? active.value : active.textContent) : ''
      if (String(held || '').trim() !== '') return false
      if (
        typeof active.closest === 'function' &&
        active.closest('dialog[open], [aria-modal="true"]')
      )
        return false
      return true
    },

    // The answers of a choice / confirm card, one row each (CHOICE_BTN), by
    // the ONE rule every surface that asks reads (functions/askCardOptions —
    // AiAsk on the island and the call reads it too): a confirm card answers
    // Confirm (the primary) or Cancel, in the tool's own words when it gave
    // them; while Change has an answered card open, its current answer is
    // the one filled capsule (no second, primary fill beside it).
    choiceOptions(s, el) {
      void s.root?.lang
      const rows = el.call('askCardOptions', s)
      return (Array.isArray(rows) ? rows : []).map((o) => ({ ...o, key: 'answer-' + o.at }))
    },

    // ── A QUESTION CARD'S FACES (Nika 2026-10-28: "colalpese after answer,
    // fix positioning and make it able to also edit"). What the card shows
    // and which field is open live on the chat's root, by card id —
    // `_aiAskCompose` (declared in the shell's state): a card row is patched
    // with `state.replace` on every message update, so a key of its own would
    // not survive. `{ field: 'own' | 'edit' | null, change: boolean }`, UI
    // state only (never persisted, gone on a workspace switch).
    askMode(s) {
      const id = s && s._cardId
      const root = s && s.root
      const all = root && root._aiAskCompose
      const mode = all && id ? all[id] : null
      return mode && typeof mode === 'object' ? mode : null
    },
    // 'open' — the question and its answers (live, or reopened by Change);
    // 'answered' — one line: the question short, the answer, Change;
    // 'closed' — a new message closed it (the transport's supersede), or its
    // turn ended without the answer (`_closedBy`): one muted line, the
    // question it closed, nothing to change.
    askFace(s) {
      const mode = this.askMode(s)
      if (s._superseded) return 'closed'
      if (s._resolved && !(mode && mode.change)) return 'answered'
      return 'open'
    },
    // The field open on an open card: 'own' (Write my own) or 'edit' (a
    // confirm card's Edit), else null.
    askField(s) {
      const mode = this.askMode(s)
      if (this.askFace(s) !== 'open' || !mode) return null
      return mode.field === 'own' || mode.field === 'edit' ? mode.field : null
    },
    // An answered card that Change reopened.
    askChanging(s) {
      const mode = this.askMode(s)
      return !!(s._resolved && !s._superseded && mode && mode.change)
    },
    // The answer on the folded line: the pick's label, the person's own
    // words, or what a confirm card's Edit asked to change.
    askAnswerText(el, s) {
      void s.root?.lang
      const said = s._chosenLabel != null ? String(s._chosenLabel).trim() : ''
      if (s._edit) {
        return String(el.call('polyglot', 'ai.chatCard.ask.editAnswer') || '').replace(
          '{words}',
          () => said
        )
      }
      if (said) return said
      if (s.type === 'confirm' && s._chosen === 'confirm') {
        return s.confirmLabel || el.call('polyglot', 'ai.chatCard.confirm')
      }
      if (s.type === 'confirm' && s._chosen === 'cancel') {
        return s.cancelLabel || el.call('polyglot', 'ai.chatCard.cancel')
      }
      return s._chosen != null ? String(s._chosen) : ''
    },
    // Why a closed card closed, in the page language (`_closedBy`, set by the
    // transport's _endWaitingCards): the person stopped the reply, or the
    // turn failed or ended first; else a new message closed it (supersede).
    askWhyText(el, s) {
      void s.root?.lang
      const key =
        s._closedBy === 'stop'
          ? 'ai.chatCard.ask.closedStop'
          : s._closedBy === 'end'
            ? 'ai.chatCard.ask.closedEnd'
            : 'ai.tool.superseded'
      return String(el.call('polyglot', key) || '')
    },
    // Write a card's mode (null clears it) — in memory only.
    askSetMode(s, mode) {
      const root = s && s.root
      const id = s && s._cardId
      if (!root || typeof root.update !== 'function' || !id) return
      const all = { ...(root._aiAskCompose || {}) }
      if (mode) all[id] = mode
      else delete all[id]
      root.update({ _aiAskCompose: all }, { preventFetch: true })
    },
    // The question card around any of its controls.
    askCard(el) {
      if (!el) return null
      if (el.key === 'PvCardChoice') return el
      return typeof el.lookup === 'function' ? el.lookup('PvCardChoice') : null
    },
    // The card's controls, by role: 'card' (the card itself), 'field' (its
    // input), 'writeOwn' (Write my own / Edit), 'change' (Change).
    askTarget(card, name) {
      if (!card) return null
      if (name === 'card') return card
      if (name === 'field') return (card.PvCompose && card.PvCompose.AppInputCompact) || null
      if (name === 'writeOwn') return (card.PvAnswers && card.PvAnswers.AppCapsuleBtnGhost) || null
      if (name === 'change') return (card.PvFolded && card.PvFolded.AppCapsuleBtnGhost) || null
      return null
    },
    // Focus a control of the card once the update shows it: `hide` applies
    // in the update's microtask, so a focus in the same tick lands on a
    // hidden node. Two microtasks later is still inside the press, so a
    // phone keyboard opens for a field.
    askFocus(card, name) {
      if (!card) return
      const run = () => {
        const target = this.askTarget(card, name)
        const node = target && target.node
        if (node && typeof node.focus === 'function') node.focus()
      }
      queueMicrotask(() => queueMicrotask(run))
    },
    // The field's words live with the card's mode (`text`), bound to the
    // input's `value`: a mode that closes takes them with it, so a closed
    // field is empty — its words were never sent, and a hidden field that
    // holds text reads as unsaved work to the shell (hasUnsavedInput asks
    // "Discard changes?" for it). Only an open field shows them: a question
    // closed under an open field (its turn stopped or ended, or another
    // surface answered it) keeps its mode, never its words.
    askText(s) {
      const mode = this.askMode(s)
      const open = this.askField(s)
      return open && mode && typeof mode.text === 'string' ? mode.text : ''
    },
    askSetText(s, text) {
      const mode = this.askMode(s)
      if (!mode || !mode.field) return
      this.askSetMode(s, { ...mode, text: String(text == null ? '' : text) })
    },
    // A press on an answer: answers the live question, or changes the answer
    // of an answered one (the AI reads the new answer as the person's next
    // message). The card folds to its answer line; focus goes to its Change.
    askPick(el, value, label) {
      const card = this.askCard(el)
      const s = card && card.state
      if (!s || !s._cardId || s._superseded) return
      const changing = this.askChanging(s)
      if (s._resolved && !changing) return
      if (this.askMode(s)) this.askSetMode(s, null)
      // the answer it already has: nothing to tell the AI
      const same =
        changing &&
        !s._written &&
        !s._edit &&
        s._chosen != null &&
        String(s._chosen) === String(value)
      if (same) return this.askFocus(card, 'change')
      try {
        if (changing) el.call('changeChatCardAnswer', s._cardId, value, label)
        else el.call('resolveChatCard', s._cardId, value, label)
      } catch (_) {}
      this.askFocus(card, 'change')
    },
    // Send the field: the person's own words as the answer (Write my own),
    // or what should change (Edit — never a yes: the transport answers the
    // tool `confirmed: false` with the words). An empty field sends nothing.
    askSubmit(el) {
      const card = this.askCard(el)
      const s = card && card.state
      const field = this.askTarget(card, 'field')
      const node = field && field.node
      const kind = s ? this.askField(s) : null
      if (!s || !s._cardId || !kind) return
      // the words on screen (the last keystroke may not have reached the
      // mode yet), else the mode's
      const typed = node && typeof node.value === 'string' ? node.value : this.askText(s)
      const text = String(typed || '').trim()
      if (!text) {
        this.askFocus(card, 'field')
        return
      }
      const changing = this.askChanging(s)
      if (s._resolved && !changing) return
      const extra = kind === 'edit' ? { edit: true, text } : { written: true, text }
      const value = kind === 'edit' ? 'edit' : text
      this.askSetMode(s, null)
      try {
        if (changing) el.call('changeChatCardAnswer', s._cardId, value, text, extra)
        else el.call('resolveChatCard', s._cardId, value, text, extra)
      } catch (_) {}
      this.askFocus(card, 'change')
    },
    // Write my own / Edit: the field opens under the answers, empty, with
    // focus.
    askOpenField(el, kind) {
      const card = this.askCard(el)
      const s = card && card.state
      if (!s || this.askFace(s) !== 'open') return
      this.askSetMode(s, { field: kind, change: this.askChanging(s), text: '' })
      this.askFocus(card, 'field')
    },
    // The field's close (and Escape): back to the answers, its words
    // dropped, focus on the control that opened it.
    askCloseField(el) {
      const card = this.askCard(el)
      const s = card && card.state
      if (!s) return
      const changing = this.askChanging(s)
      this.askSetMode(s, changing ? { field: null, change: true } : null)
      this.askFocus(card, 'writeOwn')
    },
    // Change: an answered card opens again, its answers and its field.
    askChange(el) {
      const card = this.askCard(el)
      const s = card && card.state
      if (!s || this.askFace(s) !== 'answered') return
      this.askSetMode(s, { field: null, change: true })
      this.askFocus(card, 'card')
    },
    // Keep the answer (and Escape while changing): the card folds again.
    askKeep(el) {
      const card = this.askCard(el)
      const s = card && card.state
      if (!s) return
      this.askSetMode(s, null)
      this.askFocus(card, 'change')
    }
  },

  width: '100%',

  // Tickets — a clickable compact card per ticket.
  PvCardTickets: {
    if: (el, s) => s.type === 'tickets',
    flow: 'y',
    gap: 'Y',
    childExtends: TICKET_CARD,
    childrenAs: 'state',
    children: (el, s) => s.items || []
  },

  // People & agents — avatar chips (robot for agents, initials for humans).
  PvCardPeople: {
    if: (el, s) => s.type === 'people',
    flow: 'x',
    gap: 'Y',
    flexWrap: 'wrap',
    childExtends: PERSON_CHIP,
    childrenAs: 'state',
    children: (el, s) => s.items || []
  },

  // Thumbnails.
  PvCardThumbs: {
    if: (el, s) => s.type === 'thumbnails',
    flow: 'x',
    gap: 'Z',
    flexWrap: 'wrap',
    childExtends: THUMB,
    childrenAs: 'state',
    children: (el, s) => s.items || []
  },

  // Policies — a compact clickable card per insurance policy; opens /policies.
  PvCardPolicies: {
    if: (el, s) => s.type === 'policies',
    flow: 'y',
    gap: 'Y',
    childExtends: POLICY_CARD,
    childrenAs: 'state',
    children: (el, s) => s.items || []
  },

  // Choice / confirm — interactive buttons that resume the AI turn on click.
  //
  // THE QUESTION TAKES THE KEYBOARD (round 1b, ledger BLD-18 — sam, keyboard
  // only: focus stayed in the composer and the answer chips were 13–14 Tab
  // stops away). The card is a group named by its prompt; a live, unanswered
  // card takes focus ONCE when it appears (scope.askTakesFocus: never from a
  // person who is typing, never from another open layer), so the next Tab is
  // the first answer and the focus says the question. A card that takes focus
  // leaves the chat's polite log (Chat.js Messages is `role: 'log'`) — the
  // focus already said it, so the question is said once; a card that does not
  // is read by the log as before. `tabindex: -1`: focusable by this move, never
  // a Tab stop of its own. A restored card (sessions.js) is always answered or
  // closed, so an unanswered card IS a live ask.
  //
  // THREE FACES (Nika 2026-10-28: "colalpese after answer, fix positioning and
  // make it able to also edit"; scope.askFace):
  //   open      the question and its answers, then Write my own (a choice
  //             card) or Edit (a confirm card), which opens a field under the
  //             answers: Enter sends, Escape or its close goes back.
  //   answered  ONE LINE — the question short and quiet, the answer, and
  //             Change, which opens the card again: a new pick or new words
  //             go to the AI as the person's next message.
  //   closed    a new message closed it, or its turn ended without the
  //             answer (a stop, a failure): one muted line — the question it
  //             closed — nothing to change.
  // Both folded faces are ONE line (PvFolded). An answered card kept both
  // answer buttons (an inert one at 0.4 opacity) and added a raw green "✓ Red"
  // under them. The folded faces take the `.isFolded` inset (a static class:
  // a reactive padding is dropped).
  PvCardChoice: {
    if: (el, s) => s.type === 'choice' || s.type === 'confirm',
    extends: 'CommonCard',
    theme: (el, s) => el.scope.surface(s),
    flow: 'y',
    gap: 'Z',
    role: 'group',
    tabindex: '-1',
    aria: {
      labelledby: (el, s) => (s.prompt && s._cardId ? 'chat-ask-' + s._cardId : null),
      live: (el, s) => (el.__askFocus || el.scope.askFocused(s) ? 'off' : null)
    },
    // The house focus ring (the `focusRing` shadow), as on every focusable card.
    ':focus-visible': { shadow: 'focusRing' },
    isFolded: (el, s) => el.scope.askFace(s) !== 'open',
    '.isFolded': { padding: 'Y Y Y Z1' },
    // Decided BEFORE the node exists, so the card is created out of the log
    // when it is going to take focus (the log never reads it first).
    onInit: (el, s) => {
      el.__askFocus = el.scope.askTakesFocus(el, s)
    },
    onMounted: (el, s) => {
      if (!el.__askFocus) return
      const node = el.node
      if (!node || typeof node.focus !== 'function') return
      const root = s && s.root
      if (root && s._cardId) root.__chatAskFocused = s._cardId
      node.focus()
    },
    // Escape while Change has the card open keeps the answer. The field
    // takes its own Escape first (PvCompose); preventDefault keeps the
    // shell's Escape (it closes the drawer) out of it.
    onKeydown: (e, el, s) => {
      if (e.key !== 'Escape' || !el.scope.askChanging(s)) return
      e.preventDefault()
      e.stopPropagation()
      el.scope.askKeep(el)
    },
    PvPrompt: {
      hide: (el, s) => !s.prompt || el.scope.askFace(s) !== 'open',
      id: (el, s) => (s._cardId ? 'chat-ask-' + s._cardId : null),
      text: (el, s) => s.prompt,
      fontSize: 'Z1',
      fontWeight: '500',
      color: 'title',
      overflowWrap: 'anywhere'
    },
    // The answers in one wrapping row: the picks, then Write my own / Edit
    // (AppCapsuleBtnGhost); while Change has the card open, a close at the
    // row's end (CloseButton) keeps the answer.
    PvAnswers: {
      hide: (el, s) => el.scope.askFace(s) !== 'open',
      flow: 'x',
      flexWrap: 'wrap',
      align: 'center flex-start',
      gap: 'Y',
      minWidth: '0',
      // The picks sit in the row itself (no box of their own), so they wrap
      // with Write my own as one row.
      PvOptions: {
        display: 'contents',
        childExtends: CHOICE_BTN,
        childrenAs: 'state',
        children: (el, s) => el.scope.choiceOptions(s, el)
      },
      // Write my own (a choice card) / Edit (a confirm card): opens the field.
      AppCapsuleBtnGhost: {
        compact: true,
        hide: (el, s) => !!el.scope.askField(s),
        Icon: { name: 'edit', fontSize: 'Z2', flexShrink: '0' },
        // Function text beside the Icon: a `{{ }}` label is rewritten by the
        // language switch through textContent, which drops the Icon.
        Label: {
          tag: 'span',
          text: (el, s) => {
            void s.root?.lang
            return el.call(
              'polyglot',
              s.type === 'confirm' ? 'common.action.edit' : 'ai.chatCard.ask.writeOwn'
            )
          }
        },
        onClick: (e, el, s) => {
          e.preventDefault()
          el.scope.askOpenField(el, s.type === 'confirm' ? 'edit' : 'own')
        }
      },
      // Keep my answer, while Change has the card open. CloseButton carries a
      // scope of its own (its localized name), so its factories reach the
      // card's helpers through the row.
      CloseButton: {
        hide: (el, s) => !el.parent.scope.askChanging(s),
        marginLeft: 'auto',
        'aria-label': (el, s) => {
          void s.root?.lang
          return el.call('polyglot', 'ai.chatCard.ask.keepAnswer')
        },
        onClick: (e, el) => {
          e.preventDefault()
          el.parent.scope.askKeep(el)
        }
      }
    },
    // The field: the person's own words (Write my own) or what should change
    // (a confirm card's Edit). Enter or Send sends; Escape and the close go
    // back.
    PvCompose: {
      hide: (el, s) => !el.scope.askField(s),
      flow: 'x',
      align: 'center',
      gap: 'Y',
      width: '100%',
      minWidth: '0',
      // AppInput carries a scope of its own (the field kit), so its factories
      // reach the card's helpers through the row. Its words are bound to the
      // card's mode (scope.askText): a mode that closes empties it.
      AppInputCompact: {
        flex: '1 1 0',
        enterkeyhint: 'send',
        autocomplete: 'off',
        value: (el, s) => el.parent.scope.askText(s),
        onInput: (e, el, s) => el.parent.scope.askSetText(s, el.node.value),
        placeholder: (el, s) => {
          void s.root?.lang
          return el.call(
            'polyglot',
            el.parent.scope.askField(s) === 'edit'
              ? 'ai.chatCard.ask.editPlaceholder'
              : 'ai.chatCard.ask.ownPlaceholder'
          )
        },
        ariaLabel: (el, s) => {
          void s.root?.lang
          return el.call(
            'polyglot',
            el.parent.scope.askField(s) === 'edit'
              ? 'ai.chatCard.ask.editPlaceholder'
              : 'ai.chatCard.ask.ownPlaceholder'
          )
        },
        onKeydown: (e, el) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
            e.preventDefault()
            el.parent.scope.askSubmit(el)
          } else if (e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
            el.parent.scope.askCloseField(el)
          }
        }
      },
      AppCapsuleBtnPrimary: {
        compact: true,
        text: '{{ common.action.send | polyglot }}',
        onClick: (e, el) => {
          e.preventDefault()
          el.scope.askSubmit(el)
        }
      },
      CloseButton: {
        onClick: (e, el) => {
          e.preventDefault()
          el.parent.scope.askCloseField(el)
        }
      }
    },
    // FOLDED → ONE LINE, the question short and quiet first (its whole text
    // on hover).
    //   answered  then the answer (primary text: it wraps, never cut) and
    //             Change (AppCapsuleBtnGhost). Under its step the row
    //             already names the answer ("You chose “Blue”",
    //             `_pickOnRow`: ChatMessage.scope.rowNamesPick), so the line
    //             keeps the question and Change; a card with no row that
    //             names it (in Cards, an Edit, words too long for the row)
    //             keeps the answer here (coordinator W3a: said once);
    //   closed    a new message closed it (the transport's supersede): no
    //             answer, nothing to change. Under its step the row already
    //             says why ("Unanswered question · Skipped — you sent a new
    //             message"), so the line names the question it closed; a card
    //             with no step row above it (in Cards: `_step` unset) also
    //             says why, in the row's own words (PvWhy). A card its turn
    //             left (`_closedBy`: a stop, a failed or ended turn) always
    //             says why, and its row reads only "Unanswered question" (no
    //             tag: the card says why — ChatToolRow SkippedTag).
    // Only the question gives way (it shortens). The answer never shrinks:
    // where the line is too narrow (390 px, measured 2026-10-28: "Red" broke
    // into "Re" / "d"), the line wraps — the answer, then Change, go under.
    PvFolded: {
      hide: (el, s) => el.scope.askFace(s) === 'open',
      flow: 'x',
      align: 'center',
      flexWrap: 'wrap',
      gap: 'Z',
      width: '100%',
      minWidth: '0',
      animationName: 'fadeIn',
      animationDuration: 'B',
      animationTimingFunction: 'defaultBezier',
      PvAsked: {
        tag: 'span',
        hide: (el, s) => !s.prompt,
        text: (el, s) => s.prompt,
        title: (el, s) => s.prompt || null,
        flex: '0 1 auto',
        minWidth: '0',
        fontSize: 'Z',
        color: 'caption',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      },
      PvAnswer: {
        tag: 'span',
        // Read before the branch, so the factory tracks it from the first
        // render (FA515).
        hide: (el, s) => {
          const step = !!s._step
          const onRow = !!s._pickOnRow
          return (step && onRow) || el.scope.askFace(s) !== 'answered'
        },
        text: (el, s) => el.scope.askAnswerText(el, s),
        flex: '0 0 auto',
        maxWidth: '100%',
        fontSize: 'Z1',
        fontWeight: '500',
        color: 'title',
        overflowWrap: 'anywhere'
      },
      PvWhy: {
        tag: 'span',
        hide: (el, s) =>
          el.scope.askFace(s) !== 'closed' || (!!s._step && !!s.prompt && !s._closedBy),
        text: (el, s) => el.scope.askWhyText(el, s),
        flex: '0 0 auto',
        maxWidth: '100%',
        fontSize: 'Z',
        color: 'caption'
      },
      AppCapsuleBtnGhost: {
        hide: (el, s) => el.scope.askFace(s) !== 'answered',
        compact: true,
        marginLeft: 'auto',
        Icon: { name: 'edit', fontSize: 'Z2', flexShrink: '0' },
        Label: { tag: 'span', text: '{{ ai.chatCard.ask.change | polyglot }}' },
        onClick: (e, el) => {
          e.preventDefault()
          el.scope.askChange(el)
        }
      }
    }
  },

  // AI action approval — the assistant proposes a change to a workspace project's
  // books and waits. Approve runs it; Reject drops it. The buttons show only while
  // THIS card is the live pending action (root._aiAction.id === s.id); once handled
  // it collapses to a ✓. approveAiAction/rejectAiAction are NOT static
  // registrations: workspaceProjects.js injects moduleActions.js's trio into
  // ctx.functions whenever manifest-v2 module actions are in scope (takeover
  // activation, and the merged installed-apps caps pass), and its teardown
  // clears root._aiAction (hiding these buttons, synchronously) BEFORE the
  // names unregister — so a rendered control always resolves. Contract pinned
  // by workspace/__tests__/chatCardModuleActionContract.test.js.
  //
  // DECIDED → ONE LINE (Nika 2026-10-28: "colalpese after answer"): once this
  // card is not the live park, it folds like an answered question card — the
  // proposal short and quiet (its whole text on hover), what the run did, and
  // Open when the run names its page. Static `.isFolded` classes: a reactive
  // padding or flow is dropped. As on the question line, only the proposal
  // gives way: a narrow line wraps the outcome under it (390 px: "✓ Added 4
  // widgets to “Q3 sales”" stood in a 97 px column, three lines tall).
  PvCardAiAction: {
    if: (el, s) => s.type === 'aiAction',
    extends: 'CommonCard',
    theme: (el, s) => el.scope.surface(s),
    flow: 'y',
    gap: 'Z',
    subscribeTo: ['_aiAction'],
    isFolded: (el, s) => !(s.root && s.root._aiAction && s.root._aiAction.id === s.id),
    '.isFolded': {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      padding: 'Y Y Y Z1'
    },
    PvAiSummary: {
      text: (el, s) => s.summary || el.call('polyglot', 'ai.chatCard.applyChange'),
      fontSize: 'Z1',
      fontWeight: '700',
      color: 'title',
      isFolded: (el, s) => !(s.root && s.root._aiAction && s.root._aiAction.id === s.id),
      title: (el, s) =>
        s.root && s.root._aiAction && s.root._aiAction.id === s.id
          ? null
          : s.summary || el.call('polyglot', 'ai.chatCard.applyChange'),
      '.isFolded': {
        flex: '0 1 auto',
        minWidth: '0',
        fontSize: 'Z',
        fontWeight: '400',
        color: 'caption',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }
    },
    PvAiActions: {
      hide: (el, s) => !(s.root && s.root._aiAction && s.root._aiAction.id === s.id),
      flow: 'x',
      gap: 'Y',
      flexWrap: 'wrap',
      // §1/§6.2 primary action = ink capsule (adaptive highlight pair — AA by
      // construction). The old solid-green fill measured 2.8:1 with white ink.
      PvApprove: {
        tag: 'button',
        type: 'button',
        padding: 'V A',
        round: 'radiusPill',
        border: 'none',
        cursor: 'pointer',
        background: 'highlight',
        color: 'highlight-reversed',
        fontSize: 'Z2',
        fontWeight: '700',
        whiteSpace: 'nowrap',
        transition: 'filter 140ms defaultBezier',
        ':hover': { filter: 'brightness(1.12)' },
        text: '{{ ai.chatCard.approve | polyglot }}',
        onClick: (e, el) => {
          e.preventDefault()
          el.call('approveAiAction')
        }
      },
      PvReject: {
        tag: 'button',
        type: 'button',
        padding: 'V A',
        round: 'radiusPill',
        border: 'none',
        cursor: 'pointer',
        background: 'overlay',
        color: 'title',
        fontSize: 'Z2',
        fontWeight: '700',
        whiteSpace: 'nowrap',
        transition: 'background 140ms defaultBezier',
        ':hover': { background: 'hover' },
        text: '{{ ai.confirm.reject | polyglot }}',
        onClick: (e, el) => {
          e.preventDefault()
          el.call('rejectAiAction')
        }
      }
    },
    // What the run did, from the card's own `outcome` (moduleActions.js
    // cardOutcomePatch — APPROVAL-CARD-SHOWS-HANDLED-AFTER-A-REFUSAL-1):
    //   { state: 'handled', text }    → ✓ <the app's words>
    //   { state: 'notApplied', text } → ✗ Not applied: <the app's reason>
    //   { state: 'rejected' }         → Rejected
    // A handled card without words (written before the field existed) keeps
    // the plain ✓ Handled. Measured on dev 2026-10-05 (Storm Tokens): a
    // REFUSED action's card said "✓ Handled" under the app's own refusal.
    // ONE LINE (APPROVAL-OUTCOME-PRINTED-TWICE-1, decided 2026-10-05): the
    // card is the only place the outcome shows — moduleActions.js no longer
    // adds a "✓ <words>" chat line under it, so the card says the words.
    PvAiHandled: {
      hide: (el, s) => !!(s.root && s.root._aiAction && s.root._aiAction.id === s.id),
      text: (el, s) => {
        const o = s.outcome && typeof s.outcome === 'object' ? s.outcome : null
        if (o && o.state === 'rejected') return el.call('polyglot', 'ai.chatCard.rejected')
        if (o && o.state === 'notApplied') {
          const label = el.call('polyglot', 'ai.chatCard.notApplied')
          const why = typeof o.text === 'string' ? o.text.trim().slice(0, 240) : ''
          return why ? `${label}: ${why}` : label
        }
        const did =
          o && o.state === 'handled' && typeof o.text === 'string'
            ? o.text.trim().slice(0, 240)
            : ''
        return did ? `✓ ${did}` : el.call('polyglot', 'ai.chatCard.handled')
      },
      // The outcome is the folded line's answer: primary text, it wraps.
      flex: '0 0 auto',
      maxWidth: '100%',
      fontSize: 'Z1',
      fontWeight: '500',
      overflowWrap: 'anywhere',
      // Ink that CARRIES MEANING takes the brand's theme-paired inks (raw
      // green / red read 2.15:1 / 2.45:1 as small text on a light ground —
      // brand designSystem/color.js), never a raw hue.
      color: (el, s) => {
        const state = s.outcome && s.outcome.state
        if (state === 'notApplied') return 'dangerInk'
        if (state === 'rejected') return 'caption'
        return 'successInk'
      }
    },
    // OPEN WHERE IT LANDED (BESSI dev 2026-10-28): an approved write that names
    // its page — `outcome.open`, which moduleActions.js _runOutcome keeps only
    // as a same-origin path — opens it in a SPLIT pane, so the thread moves
    // beside the page instead of giving way to it.
    // Shown only on a handled card, so always on the folded line: its end.
    AppCapsuleBtnGhost: {
      compact: true,
      marginLeft: 'auto',
      hide: (el, s) =>
        !(s.outcome && s.outcome.state === 'handled' && typeof s.outcome.open === 'string'),
      Label: { tag: 'span', text: '{{ common.action.open | polyglot }}' },
      Icon: { name: 'arrowRight', fontSize: 'Z2', flexShrink: '0' },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        if (s.outcome && typeof s.outcome.open === 'string') {
          el.call('openAppPage', s.outcome.open, { split: true })
        }
      }
    }
  },

  // INLINE GENERATIVE BLOCKS (AI_ASSISTANT_SPEC v1 §3) — an answer VIEW
  // (render_answer_ui), a RECORDS block, a generated BOARD, an installed app's
  // WIDGET or VIEW (embed_widget / embed_view), an app's own FORM from a
  // proposal (embed_form), WEB SOURCES (web_search / fetch_url) — drawn by
  // the workspace shell's own
  // AssistantThreadBlock on this card's surface. Only a host that registers
  // that component renders them; a host without it (the docs, the preview,
  // the extension panels) reads them through the fallback below instead of an
  // empty card.
  PvCardBlock: {
    if: (el, s) =>
      ['view', 'records', 'board', 'widget', 'form', 'sources', 'capabilities'].includes(s.type) &&
      !!(el.context && el.context.components && el.context.components.AssistantThreadBlock),
    extends: 'AssistantThreadBlock',
    theme: (el, s) => el.scope.surface(s)
  },

  // Fallback — a card whose `type` no branch above claims. The dispatcher used
  // to be silent-empty here (a zero-height div): the model invented a type or
  // sent a malformed card, the tool reported ✓, and nothing appeared — the
  // worst failure mode because nobody sees it. Render the best generic reading
  // of the payload instead (item labels, or the card's own text), and warn so
  // the unknown type is discoverable in the console.
  PvCardFallback: {
    if: (el, s) =>
      !['tickets', 'people', 'thumbnails', 'policies', 'choice', 'confirm', 'aiAction'].includes(
        s.type
      ) &&
      !(
        ['view', 'records', 'board', 'widget', 'form', 'sources', 'capabilities'].includes(
          s.type
        ) && !!(el.context && el.context.components && el.context.components.AssistantThreadBlock)
      ),
    extends: 'CommonCard',
    theme: (el, s) => el.scope.surface(s),
    flow: 'y',
    gap: 'Y',
    onCreate: (el, s) => {
      console.warn('[ChatCard] unknown card type — rendering generic fallback:', s.type, s)
    },
    PvFallbackRows: {
      flow: 'y',
      gap: 'X',
      childrenAs: 'state',
      children: (el, s) => {
        const items = Array.isArray(s.items) ? s.items : []
        if (items.length) {
          return items.slice(0, 12).map((item, i) => ({
            key: `fb-${i}`,
            label:
              (item && (item.title || item.name || item.label || item.caption || item.text)) ||
              (typeof item === 'string' ? item : JSON.stringify(item).slice(0, 80)),
            sub: (item && (item.state || item.role || item.email || item.status)) || ''
          }))
        }
        const own = s.text || s.title || s.summary || s.prompt
        return own ? [{ key: 'fb-0', label: String(own), sub: '' }] : []
      },
      childExtends: {
        flow: 'x',
        gap: 'Z',
        align: 'center',
        minWidth: '0',
        // The action's own words — primary text: it wraps (rule 8).
        PvFbLabel: {
          tag: 'span',
          fontSize: 'Z1',
          color: 'title',
          flex: '1 1 auto',
          minWidth: '0',
          overflowWrap: 'anywhere',
          text: (el, s) => s.label
        },
        PvFbSub: {
          if: (el, s) => !!s.sub,
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          flexShrink: '0',
          text: (el, s) => s.sub
        }
      }
    },
    PvFallbackNote: {
      text: (el, s) => `unsupported card type “${s.type || 'none'}”`,
      fontSize: 'Y2',
      color: 'caption',
      opacity: '.6'
    }
  }
}
